// Stripe return handling. The existing API builds success URLs on the venue's own origin
// (e.g. {origin}/booking-payment-success?order_id=&session_id=), so the venue site's return pages
// embed this same loader; we detect the params, confirm the payment, and show the result.
import { Api } from './api';
import type { EmbedConfig, ThemeOptions } from './types';
import { createRoot, h, footer, clear, renderError } from './ui';

/**
 * Stripe-return safety net. In the redirect-to-365 model the transactional step happens FIRST-PARTY on
 * the platform, so its Stripe success URLs return to the PLATFORM (which confirms + bounces the customer
 * back to the venue with ?booking=success), not here. This only fires in the unlikely case a success URL
 * still lands on the venue origin with order_id+session_id — we confirm + show a friendly message.
 * Returns true if this load was such a return (loader then skips normal widget mounting).
 */
export async function handleReturn(api: Api, _cfg: EmbedConfig, theme: ThemeOptions): Promise<boolean> {
  const q = new URLSearchParams(location.search);
  if (q.get('payment') === 'cancelled') return false; // fall through to a normal widget render

  const sessionId = q.get('session_id');
  const orderId = q.get('order_id');

  if (orderId && sessionId) {
    const mount = pickMount('booking');
    const root = mount ? createRoot(mount, theme).root : null;
    if (root) { clear(root); root.append(h('h3', undefined, 'Confirming your booking…')); }
    try {
      await api.post(`/api/bookings/orders/${orderId}/confirm-payment`, sessionId);
      if (root) {
        clear(root);
        root.append(
          h('h3', undefined, 'You’re booked!'),
          h('div', { class: 'e365-alert info' }, 'Your reservation is confirmed. Check your email for details.'),
          footer());
      }
    } catch {
      if (root) renderError(root, 'We received your payment — your booking is being confirmed. Check your email shortly.');
    }
    return true;
  }

  return false;
}

function pickMount(widget: string): HTMLElement | null {
  return (document.querySelector(`[data-365="${widget}"]`) as HTMLElement | null)
    ?? (document.querySelector('[data-365]') as HTMLElement | null);
}
